/**
 * Canvas Tabs
 *
 * Tab bar for switching between multiple canvases. Only shown when there
 * are 2+ canvases. The default canvas renders as a sticky icon-only
 * button on the left (always visible, never scrolls), like a browser pinned
 * tab; other canvases are draggable text tabs in the scrollable strip.
 *
 * The strip scrolls horizontally when tabs don't all fit, the active tab
 * scrolls into view on selection, and non-default tabs that are clipped are
 * surfaced through a chevron overflow menu pinned to the right.
 */

import { cleanDisplayText } from '@/utils/display-text';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, Square, X } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { InlineEdit } from '../ui/InlineEdit';
import type { Canvas } from '../../types/canvases';

interface CanvasTabsProps {
  canvases: Canvas[];
  activeCanvasId: string | null;
  pendingUpdates?: Map<string, string>;
  onSelect: (canvasId: string) => void;
  onDismiss?: (canvasId: string) => void;
  onReorder?: (orderedNonDefaultIds: string[]) => void;
  onRename?: (canvasId: string, title: string) => Promise<void> | void;
  rightSlot?: React.ReactNode;
}

export function CanvasTabs({
  canvases,
  activeCanvasId,
  pendingUpdates,
  onSelect,
  onDismiss,
  onReorder,
  onRename,
  rightSlot,
}: CanvasTabsProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const rafRef = useRef<number>(0);
  const [overflowIds, setOverflowIds] = useState<Set<string>>(new Set());
  const [leftClipped, setLeftClipped] = useState(false);
  const [rightClipped, setRightClipped] = useState(false);

  const defaultCv = canvases.find((w) => w.isDefault);
  const others = canvases.filter((w) => !w.isDefault);
  const orderedIds = others.map((w) => w.canvasId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const measure = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const stripRect = strip.getBoundingClientRect();
    const rightLimit = stripRect.right;
    const leftLimit = stripRect.left;

    const next = new Set<string>();
    let anyLeftClip = false;
    let anyRightClip = false;
    for (const cv of canvases) {
      const el = tabRefs.current.get(cv.canvasId);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const offRight = rect.right > rightLimit;
      const offLeft = rect.left < leftLimit;
      if (offRight) anyRightClip = true;
      if (offLeft) anyLeftClip = true;
      if (offRight || offLeft) next.add(cv.canvasId);
    }

    setOverflowIds((prev) => {
      if (prev.size === next.size && [...prev].every((id) => next.has(id))) return prev;
      return next;
    });
    setLeftClipped(anyLeftClip);
    setRightClipped(anyRightClip);
  }, [canvases, others.length]);

  useLayoutEffect(() => {
    measure();
    const strip = stripRef.current;
    if (!strip) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(strip);
    strip.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      strip.removeEventListener('scroll', measure);
    };
  }, [measure]);

  // Scroll the active tab into view when activation changes. Manual scrollBy
  // (instead of Element.scrollIntoView) keeps the page from scrolling
  // vertically and respects the chevron's reserved area on the right edge.
  // Double-RAF mirrors CanvasPanel's scroll-to-surface so any layout
  // reflow from the canvas switch settles before measuring.
  useEffect(() => {
    if (!activeCanvasId) return;
    const strip = stripRef.current;
    const tab = tabRefs.current.get(activeCanvasId);
    if (!strip || !tab) return;

    const r1 = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        const stripRect = strip.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        // Reserve space on either side so the active tab doesn't land
        // underneath the left/right fade gradients (w-8 = 32px) when other
        // tabs remain clipped off-screen. Includes a small breathing
        // margin on top of the fade width.
        const fadeReserve = 40;

        const leftOverflow = tabRect.left - (stripRect.left + fadeReserve);
        const rightOverflow = tabRect.right - (stripRect.right - fadeReserve);

        let delta = 0;
        if (leftOverflow < 0) delta = leftOverflow;
        else if (rightOverflow > 0) delta = rightOverflow;

        // Snap to the scroll extremes when within the strip's horizontal
        // padding, so the leftmost tab gets its natural left padding back
        // (and likewise for the rightmost). Without this, scrolling the
        // first tab into view leaves scrollLeft equal to the padding (16px),
        // which clips the strip's leading whitespace.
        const maxScroll = strip.scrollWidth - strip.clientWidth;
        const target = strip.scrollLeft + delta;
        const SNAP = 24;
        let finalScroll = target;
        if (finalScroll <= SNAP) finalScroll = 0;
        else if (finalScroll >= maxScroll - SNAP) finalScroll = maxScroll;

        if (finalScroll !== strip.scrollLeft) {
          strip.scrollTo({ left: finalScroll, behavior: 'smooth' });
        }
      });
    });
    rafRef.current = r1;
    return () => cancelAnimationFrame(rafRef.current);
  }, [activeCanvasId]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = orderedIds.indexOf(String(active.id));
    const newIdx = orderedIds.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(orderedIds, oldIdx, newIdx);
    onReorder?.(next);
  };

  if (canvases.length === 0) return null;

  // Exclude the active canvas from the overflow menu — the scroll-into-view
  // effect ensures the active tab is visible in the strip, so listing it in
  // the dropdown would be a confusing duplicate. Filtering here also covers
  // the few frames during smooth-scroll where the active tab is still
  // partially clipped. The default canvas is rendered outside the strip
  // (sticky pinned slot), so it never registers a tabRef and naturally
  // never appears in this list.
  const overflowedCanvases = canvases.filter(
    (w) => overflowIds.has(w.canvasId) && w.canvasId !== activeCanvasId,
  );

  const setTabRef = (canvasId: string) => (el: HTMLDivElement | null) => {
    if (el) tabRefs.current.set(canvasId, el);
    else tabRefs.current.delete(canvasId);
  };

  return (
    <div className="flex h-16 shrink-0 items-stretch border-b border-border">
      {defaultCv && (
        <div className="flex shrink-0 items-center py-2 pl-3 pr-1">
          <PinnedDefaultTab
            canvas={defaultCv}
            isActive={defaultCv.canvasId === activeCanvasId}
            isPending={!!pendingUpdates?.has(defaultCv.canvasId)}
            onSelect={onSelect}
          />
        </div>
      )}
      <div className="relative flex min-w-0 flex-1 items-stretch">
        <div
          ref={stripRef}
          className="no-scrollbar flex flex-1 items-center gap-1 overflow-x-auto px-4 py-2"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={dndModifiers}
          >
            <SortableContext items={orderedIds} strategy={horizontalListSortingStrategy}>
              {others.map((cv) => (
                <SortableCanvasTab
                  key={cv.canvasId}
                  ref={setTabRef(cv.canvasId)}
                  canvas={cv}
                  isActive={cv.canvasId === activeCanvasId}
                  isPending={!!pendingUpdates?.has(cv.canvasId)}
                  onSelect={onSelect}
                  onDismiss={onDismiss}
                  onRename={onRename}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        {leftClipped && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-background to-transparent"
          />
        )}
        {rightClipped && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-background to-transparent"
          />
        )}
      </div>
      {/* Always reserved so the strip's clientWidth doesn't change as the
          chevron appears/disappears during scroll-into-view. The visible
          chevron is conditional inside the always-rendered slot. The
          rightSlot lets parents (e.g. CanvasPanel) pin status content
          like a connection indicator next to the chevron. */}
      <div className="flex shrink-0 items-center justify-end gap-2 pr-8">
        {overflowedCanvases.length > 0 && (
          <OverflowMenu
            canvases={overflowedCanvases}
            activeCanvasId={activeCanvasId}
            pendingUpdates={pendingUpdates}
            onSelect={onSelect}
          />
        )}
        {rightSlot}
      </div>
    </div>
  );
}

// Lock the dragged tab to the strip's row so it can't fly above/below the
// menu bar while reordering. dnd-kit's default allows free movement on both
// axes; we only want horizontal sortable reordering here.
const dndModifiers = [
  ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
    ...transform,
    y: 0,
  }),
];

interface TabProps {
  canvas: Canvas;
  isActive: boolean;
  isPending: boolean;
  onSelect: (canvasId: string) => void;
  onDismiss?: (canvasId: string) => void;
  onRename?: (canvasId: string, title: string) => Promise<void> | void;
}

const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const DEFAULT_TAB_SHORTCUT = IS_MAC ? '⌥ 1' : 'Alt+1';

function PinnedDefaultTab({ canvas, isActive, isPending, onSelect }: TabProps) {
  const label = cleanDisplayText(canvas.title) || 'Main canvas';
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            onClick={() => onSelect(canvas.canvasId)}
            className={`focus-ring relative flex h-6 w-6 items-center justify-center rounded ${
              isActive ? 'bg-muted text-primary' : 'text-foreground hover:bg-muted'
            }`}
          >
            <Square className="h-3.5 w-3.5" />
            {isPending && !isActive && (
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary animate-pulse" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="flex items-center gap-2">
          <span>{label}</span>
          <span className="font-mono text-xs opacity-70">{DEFAULT_TAB_SHORTCUT}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const SortableCanvasTab = forwardRef<HTMLDivElement, TabProps>(
  function SortableCanvasTab(
    { canvas, isActive, isPending, onSelect, onDismiss, onRename },
    externalRef,
  ) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: canvas.canvasId,
    });

    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);
        if (typeof externalRef === 'function') externalRef(node);
        else if (externalRef) externalRef.current = node;
      },
      [setNodeRef, externalRef],
    );

    // When dragging, drop the scaleX/scaleY that dnd-kit's derivedTransform
    // bakes in (= newRect.width / oldRect.width), which is what was squishing
    // the dragged tab's text whenever it crossed a tab of a different width.
    // Translate-only keeps the dragged tab the same size as its rest state.
    const style: React.CSSProperties = {
      transform:
        isDragging && transform
          ? `translate3d(${transform.x}px, 0px, 0)`
          : CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 10 : undefined,
    };

    const canRename = isActive && !!onRename;
    // Use a div with role="button" rather than a real <button> so the
    // active-tab can host InlineEdit's <input> without nesting interactive
    // content inside a button (which is invalid HTML).
    const handleActivate = () => onSelect(canvas.canvasId);

    return (
      <div ref={setRef} style={style} className="flex shrink-0 items-center">
        <div
          className={`group flex items-center ${
            isDragging ? 'rounded bg-background text-foreground shadow-md ring-1 ring-primary' : ''
          }`}
        >
          <div
            className={`focus-ring flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded px-2 py-0.5 text-sm font-medium focus:outline-none ${
              isActive
                ? 'bg-muted text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            onClick={handleActivate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate();
              }
            }}
            {...attributes}
            {...listeners}
          >
            {canRename ? (
              <InlineEdit
                value={canvas.title || ''}
                placeholder="Canvas"
                onSave={(t) => onRename!(canvas.canvasId, t)}
                inputClassName="text-sm font-medium min-w-[6ch]"
                hideHoverHint
              />
            ) : (
              <span>{cleanDisplayText(canvas.title) || 'Canvas'}</span>
            )}
            {isPending && !isActive && (
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            )}
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(canvas.canvasId);
              }}
              className="focus-ring ml-0.5 flex items-center justify-center rounded p-0.5 opacity-0 transition-opacity hover:bg-foreground/10 focus:opacity-100 group-hover:opacity-100"
              aria-label={`Dismiss ${cleanDisplayText(canvas.title) || 'canvas'}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    );
  },
);

interface OverflowMenuProps {
  canvases: Canvas[];
  activeCanvasId: string | null;
  pendingUpdates?: Map<string, string>;
  onSelect: (canvasId: string) => void;
}

const OverflowMenu = forwardRef<HTMLButtonElement, OverflowMenuProps>(
  function OverflowMenu(
    { canvases, activeCanvasId, pendingUpdates, onSelect },
    ref,
  ) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={ref}
            type="button"
            aria-label={`More canvases (${canvases.length})`}
            className="focus-ring flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronDown className="h-4 w-4" />
            <span className="text-xs">{canvases.length}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canvases.map((cv) => (
            <DropdownMenuItem
              key={cv.canvasId}
              onSelect={() => onSelect(cv.canvasId)}
              className={cv.canvasId === activeCanvasId ? 'bg-muted text-foreground' : ''}
            >
              <span className="truncate">{cleanDisplayText(cv.title) || 'Canvas'}</span>
              {pendingUpdates?.has(cv.canvasId) &&
                cv.canvasId !== activeCanvasId && (
                  <span className="ml-auto h-2 w-2 rounded-full bg-primary animate-pulse" />
                )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
